import {
  Component, OnInit, OnDestroy, ChangeDetectorRef
} from '@angular/core';
import { NgIf, NgFor } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import katex from 'katex';
import { Card, ExplanationBlock } from '../../services/data.service';
import {
  BilingualJsonLoaderService, BilingualDoc,
  NavLeaf, NavSection, NavSubject
} from '../../services/bilingual-json-loader.service';

const LS_NAV    = 'ocrPreview_navFlex';
const LS_MAIN   = 'ocrPreview_mainFlex';
const LS_DETAIL = 'ocrPreview_detailFlex';

@Component({
  selector: 'app-ocr-preview',
  standalone: true,
  imports: [NgIf, NgFor, FormsModule],
  templateUrl: './ocr-preview.component.html',
  styleUrls: ['./ocr-preview.component.css']
})
export class OcrPreviewComponent implements OnInit, OnDestroy {

  // ── Defaults ──────────────────────────────────────────────
  readonly DEFAULT_NAV    = 22;
  readonly DEFAULT_MAIN   = 42;
  readonly DEFAULT_DETAIL = 36;
  readonly MIN_NAV    = 14;  readonly MAX_NAV    = 40;
  readonly MIN_MAIN   = 20;  readonly MAX_MAIN   = 65;

  // ── Pane flex strings ─────────────────────────────────────
  navFlex    = `0 0 ${this.DEFAULT_NAV}%`;
  mainFlex   = `0 0 ${this.DEFAULT_MAIN}%`;
  detailFlex = `0 0 ${this.DEFAULT_DETAIL}%`;

  // ── Nav collapse ──────────────────────────────────────────
  navCollapsed = false;

  // ── Dragging: nav divider ─────────────────────────────────
  isNavDragging  = false;
  private startNavX = 0;
  private curNavW   = this.DEFAULT_NAV;
  private curMainW  = this.DEFAULT_MAIN;
  private curDetW   = this.DEFAULT_DETAIL;

  // ── Dragging: main/detail divider ────────────────────────
  isMainDragging  = false;
  private startMainX = 0;
  private snapMainW  = this.DEFAULT_MAIN;
  private snapDetW   = this.DEFAULT_DETAIL;

  // ── Reset notification ────────────────────────────────────
  showResetNotification = false;

  // ── Bilingual data ───────────────────────────────────────
  bilingualDoc: BilingualDoc | null = null;
  selectedChunkIndex = -1;
  breadcrumbSection = '';
  breadcrumbTopic = '';

  // ── Data & state ──────────────────────────────────────────
  questions: Card[] = [];
  selectedQuestion: Card | null = null;
  isLoading = false;
  showAnswer = false;
  showDetails = false;

  // ── Toolbar controls ──────────────────────────────────────
  displayMode: 'all' | 'collapseAll' | 'expandAll' = 'all';
  language: 'english' | 'bilingual' = 'english';
  optionsVertical = false;
  sideBySide = false;

  // ── Details pane options ──────────────────────────────────
  showQuestionAlso = true;
  showHindi = false;

  nav: NavSubject[] = [];

  // ── Bound handlers ────────────────────────────────────────
  private navMoveH = (e: MouseEvent) => this.onNavMouseMove(e);
  private navUpH   = ()               => this.onNavMouseUp();
  private mainMoveH = (e: MouseEvent) => this.onMainMouseMove(e);
  private mainUpH   = ()               => this.onMainMouseUp();

  constructor(
    private bilingualLoader: BilingualJsonLoaderService,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.loadSizes();
    this.isLoading = true;
    this.bilingualLoader.load().subscribe({
      next: (doc) => {
        this.bilingualDoc = doc;
        this.nav = this.bilingualLoader.buildNavTree(doc);
        this._selectFirstLeaf();
      },
      error: () => { this.isLoading = false; },
    });
  }

  ngOnDestroy(): void {
    document.removeEventListener('mousemove', this.navMoveH);
    document.removeEventListener('mouseup',   this.navUpH);
    document.removeEventListener('mousemove', this.mainMoveH);
    document.removeEventListener('mouseup',   this.mainUpH);
  }

  // ── Data loading ──────────────────────────────────────────

  private _selectFirstLeaf(): void {
    const doc = this.bilingualDoc;
    if (!doc || !this.nav.length) { this.isLoading = false; return; }
    const firstSubj = this.nav[0];
    const firstSec  = firstSubj?.sections[0];
    const firstLeaf = firstSec?.children[0];
    if (!firstLeaf) { this.isLoading = false; return; }
    firstLeaf.selected = true;
    this.selectedChunkIndex = firstLeaf.chunkIndex;
    this.breadcrumbSection = firstSec.label;
    this.breadcrumbTopic   = firstLeaf.label;
    const chunk = doc.topics.find(t => t.chunk_index === firstLeaf.chunkIndex);
    this.questions = chunk ? this.bilingualLoader.getCardsForChunk(chunk) : [];
    this.isLoading = false;
  }

  // ── Nav tree ──────────────────────────────────────────────

  selectLeaf(subject: NavSubject, section: NavSection, leaf: NavLeaf): void {
    this.nav.forEach(s => s.sections.forEach(sec => sec.children.forEach(c => c.selected = false)));
    leaf.selected = true;
    this.selectedChunkIndex = leaf.chunkIndex;
    this.breadcrumbSection  = section.label;
    this.breadcrumbTopic    = leaf.label;
    this.selectedQuestion   = null;
    this.showDetails        = false;
    const chunk = this.bilingualDoc?.topics.find(t => t.chunk_index === leaf.chunkIndex);
    this.questions = chunk ? this.bilingualLoader.getCardsForChunk(chunk) : [];
    this.cdr.detectChanges();
  }

  toggleNavCollapse(): void {
    this.navCollapsed = !this.navCollapsed;
    this.navFlex = this.navCollapsed ? '0 0 48px' : `0 0 ${this.curNavW}%`;
    this.cdr.detectChanges();
  }

  // ── Question selection ────────────────────────────────────

  selectQuestion(q: Card): void {
    this.selectedQuestion = q;
    this.showDetails = true;
  }

  closeDetails(): void {
    this.selectedQuestion = null;
    this.showDetails = false;
  }

  // ── Toolbar actions ───────────────────────────────────────

  onDisplayModeChange(): void {
    if (this.displayMode === 'collapseAll') {
      this.questions.forEach(q => q.collapsed = true);
    } else if (this.displayMode === 'expandAll') {
      this.questions.forEach(q => q.collapsed = false);
    }
  }

  toggleCardCollapse(q: Card, event: MouseEvent): void {
    event.stopPropagation();
    q.collapsed = !q.collapsed;
  }

  // ── Answer toggle ─────────────────────────────────────────

  toggleAnswer(): void { this.showAnswer = !this.showAnswer; }

  getCorrectLetter(card: Card): string {
    const m = (card.answerText || '').match(/\(([a-dA-D])\)/);
    return m ? m[1].toUpperCase() : '';
  }

  isCorrect(card: Card, letter: string): boolean {
    return this.showAnswer && this.getCorrectLetter(card) === letter.toUpperCase();
  }

  getBlocks(card: Card): ExplanationBlock[] { return card.explanationBlocks || []; }

  fmt(text: string | undefined | null): SafeHtml {
    if (!text) return this.sanitizer.bypassSecurityTrustHtml('');

    // Step 1: escape HTML entities in raw text (will be un-escaped for LaTeX segments)
    const escapeHtml = (s: string) =>
      s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    // Step 2: render KaTeX for $...$ and $$...$$ segments
    const renderLatex = (s: string): string => {
      // Handle $$...$$ display blocks first
      s = s.replace(/\$\$(.+?)\$\$/gs, (_m, expr) => {
        try {
          return katex.renderToString(expr.trim(), { displayMode: true, throwOnError: false });
        } catch { return escapeHtml(_m); }
      });
      // Handle $...$ inline math
      s = s.replace(/\$([^$\n]+?)\$/g, (_m, expr) => {
        try {
          return katex.renderToString(expr.trim(), { displayMode: false, throwOnError: false });
        } catch { return escapeHtml(_m); }
      });
      return s;
    };

    // Step 3: split on LaTeX segments so we only escape non-math parts
    // Strategy: escape the whole string, then run KaTeX on $...$ — but KaTeX
    // needs the raw LaTeX, not HTML-escaped. So we split on $ boundaries first.
    const parts = text.split(/(\$\$[\s\S]+?\$\$|\$[^$\n]+?\$)/g);
    const html = parts.map((part, idx) => {
      if (idx % 2 === 1) {
        // Math segment — render with KaTeX (no HTML escaping needed inside)
        return renderLatex(part);
      }
      // Plain text segment — escape, then apply bold + newlines
      return escapeHtml(part)
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\n/g, '<br>');
    }).join('');

    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  // ── Nav divider drag ──────────────────────────────────────

  onNavMouseDown(event: MouseEvent): void {
    event.preventDefault();
    this.isNavDragging = true;
    this.startNavX = event.clientX;
    this.curNavW  = this.parsePct(this.navFlex);
    this.curMainW = this.parsePct(this.mainFlex);
    this.curDetW  = this.parsePct(this.detailFlex);
    document.body.classList.add('user-select-none');
    document.addEventListener('mousemove', this.navMoveH);
    document.addEventListener('mouseup',   this.navUpH);
  }

  private onNavMouseMove(event: MouseEvent): void {
    if (!this.isNavDragging) return;
    const dx  = event.clientX - this.startNavX;
    const ctr = document.querySelector('.ocr-layout') as HTMLElement;
    if (!ctr) return;
    const pct = (dx / ctr.offsetWidth) * 100;
    const nw  = Math.max(this.MIN_NAV, Math.min(this.curNavW + pct, this.MAX_NAV));
    this.navFlex = `0 0 ${nw.toFixed(2)}%`;
    this.cdr.detectChanges();
  }

  private onNavMouseUp(): void {
    if (!this.isNavDragging) return;
    this.isNavDragging = false;
    document.body.classList.remove('user-select-none');
    document.removeEventListener('mousemove', this.navMoveH);
    document.removeEventListener('mouseup',   this.navUpH);
    this.curNavW = this.parsePct(this.navFlex);
    this.saveSizes();
  }

  resetNavSize(): void {
    this.curNavW = this.DEFAULT_NAV;
    this.navFlex = `0 0 ${this.DEFAULT_NAV}%`;
    this.flashReset();
    this.saveSizes();
  }

  // ── Main/Detail divider drag ──────────────────────────────

  onMainMouseDown(event: MouseEvent): void {
    event.preventDefault();
    this.isMainDragging = true;
    this.startMainX = event.clientX;
    this.snapMainW  = this.parsePct(this.mainFlex);
    this.snapDetW   = this.parsePct(this.detailFlex);
    document.body.classList.add('user-select-none');
    document.addEventListener('mousemove', this.mainMoveH);
    document.addEventListener('mouseup',   this.mainUpH);
  }

  private onMainMouseMove(event: MouseEvent): void {
    if (!this.isMainDragging) return;
    const dx  = event.clientX - this.startMainX;
    const ctr = document.querySelector('.ocr-layout') as HTMLElement;
    if (!ctr) return;
    const pct = (dx / ctr.offsetWidth) * 100;
    // moving right → details shrink; moving left → details grow
    const nd = Math.max(15, Math.min(this.snapDetW - pct, 60));
    this.detailFlex = `0 0 ${nd.toFixed(2)}%`;
    this.cdr.detectChanges();
  }

  private onMainMouseUp(): void {
    if (!this.isMainDragging) return;
    this.isMainDragging = false;
    document.body.classList.remove('user-select-none');
    document.removeEventListener('mousemove', this.mainMoveH);
    document.removeEventListener('mouseup',   this.mainUpH);
    this.curDetW = this.parsePct(this.detailFlex);
    this.saveSizes();
  }

  resetMainSize(): void {
    this.curDetW    = this.DEFAULT_DETAIL;
    this.detailFlex = `0 0 ${this.DEFAULT_DETAIL}%`;
    this.flashReset();
    this.saveSizes();
  }

  // ── LocalStorage ──────────────────────────────────────────

  private saveSizes(): void {
    try {
      localStorage.setItem(LS_NAV,    this.navFlex);
      localStorage.setItem(LS_DETAIL, this.detailFlex);
    } catch {}
  }

  private loadSizes(): void {
    try {
      const n = localStorage.getItem(LS_NAV);
      const d = localStorage.getItem(LS_DETAIL);
      if (n) { this.navFlex    = n; this.curNavW  = this.parsePct(n); }
      if (d) { this.detailFlex = d; this.curDetW  = this.parsePct(d); }
    } catch {}
  }

  private parsePct(flex: string): number {
    const m = flex.match(/(\d+(?:\.\d+)?)%/);
    return m ? parseFloat(m[1]) : 0;
  }

  // ── Reset notification ────────────────────────────────────

  private flashReset(): void {
    this.showResetNotification = true;
    this.cdr.detectChanges();
    setTimeout(() => {
      this.showResetNotification = false;
      this.cdr.detectChanges();
    }, 2000);
  }
}
